import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CalendarTodayOutlined,
  CloseRounded,
  DoneRounded,
  FactCheckOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import api from '../../utils/Api';
import {
  WEEK_DAYS,
  getApiErrorMessage,
  getClassDisplayName,
  getSchoolId,
  getStoredTeacher,
  teacherPortalTableCardSx,
} from './teacherPortalUtils';

const normalizeStatus = (value) => {
  if (value === true || value === 'present') return 'present';
  if (value === false || value === 'absent') return 'absent';
  return 'notmarked';
};

const getAttendanceWeekParam = (weekLabel) => String(weekLabel || '').replace('Week ', '');

export default function TeacherStudentAttendanceScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const schoolId = getSchoolId(teacher);
  const classes = Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : [];
  const [terms, setTerms] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');

  const selectedTerm = useMemo(
    () => terms.find((term) => term._id === selectedTermId),
    [selectedTermId, terms]
  );

  const loadTerms = useCallback(async () => {
    if (!schoolId) return;
    setError('');
    try {
      const res = await api.get('/term/all', { params: { school: schoolId } });
      const nextTerms = res.data?.allTerms || res.data?.terms || [];
      setTerms(nextTerms);
      setSelectedTermId((prev) => prev || nextTerms[0]?._id || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load terms.'));
    }
  }, [schoolId]);

  const loadWeeks = useCallback(async () => {
    if (!schoolId || !selectedTerm) return;
    try {
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear: selectedTerm.academicYear,
          term: selectedTerm.term,
          termId: selectedTerm._id,
        },
      });
      const fetchedWeeks = res.data?.weeks || [];
      const labels = fetchedWeeks
        .map((week) => week.label)
        .filter(Boolean)
        .sort((a, b) => Number(getAttendanceWeekParam(a)) - Number(getAttendanceWeekParam(b)));
      const currentWeekLabel = fetchedWeeks.find((week) => week?.isCurrent && week?.label)?.label;

      setWeeks(labels);
      setSelectedWeek(currentWeekLabel || labels[0] || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load weeks.'));
    }
  }, [schoolId, selectedTerm]);

  const loadAttendance = useCallback(async () => {
    if (!schoolId || !selectedClassId || !selectedTerm || !selectedWeek) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/attendance/by-class', {
        params: {
          classId: selectedClassId,
          termId: selectedTerm._id,
          week: getAttendanceWeekParam(selectedWeek),
          academicYear: selectedTerm.academicYear,
          school: schoolId,
        },
      });
      const nextRecords = Array.isArray(res.data)
        ? res.data.map((item) => {
            const days = item.days || {};
            const attendanceStatus = {
              M: normalizeStatus(days.M),
              T: normalizeStatus(days.T),
              W: normalizeStatus(days.W),
              TH: normalizeStatus(days.TH),
              F: normalizeStatus(days.F),
            };
            return {
              _id: item._id || item.studentId,
              studentId: item.studentId || item._id,
              studentName: item.name || item.studentName || item.student?.user?.name || 'Unknown',
              attendanceStatus,
            };
          })
        : [];
      nextRecords.sort((a, b) => a.studentName.localeCompare(b.studentName));
      setRecords(nextRecords);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load attendance.'));
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClassId, selectedTerm, selectedWeek]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  useEffect(() => {
    setSelectedWeek('');
    loadWeeks();
  }, [loadWeeks]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const markAttendance = async (studentId, dayKey, status) => {
    if (!selectedTerm || !selectedWeek) return;
    const markId = `${studentId}-${dayKey}`;
    setSavingId(markId);
    setError('');
    try {
      await api.post('/attendance/mark', {
        week: getAttendanceWeekParam(selectedWeek),
        term: selectedTerm.term,
        academicYear: selectedTerm.academicYear,
        classId: selectedClassId,
        termId: selectedTerm._id,
        attendanceUpdates: [{ studentId, days: { [dayKey]: status } }],
      });
      setRecords((prev) =>
        prev.map((record) =>
          record.studentId === studentId
            ? {
                ...record,
                attendanceStatus: {
                  ...record.attendanceStatus,
                  [dayKey]: status,
                },
              }
            : record
        )
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save attendance mark.'));
    } finally {
      setSavingId('');
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Student Attendance
          </Typography>
          <Typography color="text.secondary">
            Mark weekly attendance for assigned classes.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshOutlined />}
          variant="outlined"
          onClick={loadAttendance}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Card sx={teacherPortalTableCardSx(theme)}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 34, md: 42 }, height: { xs: 34, md: 42 } }}>
              <CalendarTodayOutlined fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Attendance Setup & Filters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the class, term, and week to mark attendance.
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  label="Class"
                  value={selectedClassId}
                  onChange={(event) => setSelectedClassId(event.target.value)}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls._id} value={cls._id}>
                      {getClassDisplayName(cls)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Term</InputLabel>
                <Select
                  label="Term"
                  value={selectedTermId}
                  onChange={(event) => setSelectedTermId(event.target.value)}
                >
                  {terms.map((term) => (
                    <MenuItem key={term._id} value={term._id}>
                      {term.term} • {term.academicYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Week</InputLabel>
                <Select
                  label="Week"
                  value={selectedWeek}
                  onChange={(event) => setSelectedWeek(event.target.value)}
                >
                  {weeks.map((week) => (
                    <MenuItem key={week} value={week}>
                      {week}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={teacherPortalTableCardSx(theme)}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'success.main', width: { xs: 34, md: 40 }, height: { xs: 34, md: 40 } }}>
                <FactCheckOutlined fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Attendance Records ({records.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedWeek || 'No week selected'}
                </Typography>
              </Box>
            </Stack>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Student</TableCell>
                    {WEEK_DAYS.map((day) => (
                      <TableCell key={day.key} align="center" sx={{ fontWeight: 900 }}>
                        {day.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.studentId}>
                      <TableCell sx={{ fontWeight: 700 }}>{record.studentName}</TableCell>
                      {WEEK_DAYS.map((day) => {
                        const current = record.attendanceStatus?.[day.key] || 'notmarked';
                        const busy = savingId === `${record.studentId}-${day.key}`;
                        return (
                          <TableCell key={day.key} align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Button
                                size="small"
                                variant={current === 'present' ? 'contained' : 'outlined'}
                                color="success"
                                disabled={busy}
                                onClick={() => markAttendance(record.studentId, day.key, 'present')}
                                sx={{ minWidth: 36 }}
                              >
                                {busy ? <CircularProgress size={14} /> : <DoneRounded fontSize="small" />}
                              </Button>
                              <Button
                                size="small"
                                variant={current === 'absent' ? 'contained' : 'outlined'}
                                color="error"
                                disabled={busy}
                                onClick={() => markAttendance(record.studentId, day.key, 'absent')}
                                sx={{ minWidth: 36 }}
                              >
                                <CloseRounded fontSize="small" />
                              </Button>
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {records.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 3 }}>
                  No attendance records found for the selected filters.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
